import { client, getToken } from "./client";

export type RecommendationRunStatus =
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED";

export type RecommendationRunStep =
  | "CLEANUP"
  | "SEGMENTS"
  | "DS_SEGMENTS"
  | "COMMERCIAL_OPPORTUNITIES"
  | "NEXT_PURCHASE"
  | "REWARDS"
  | "PROMOTIONS"
  | "ACTIVATION_ACTIONS";

export interface RecommendationRun {
  id: number;
  organization_id: number;
  status: RecommendationRunStatus;
  current_step?: RecommendationRunStep;
  current_step_number?: number;
  total_steps: number;
  current_step_label?: string;
  current_step_label_key?: string;
  current_step_label_params?: Record<string, unknown> | string;
  current_step_detail?: string;
  segment_task_id?: string;
  percent_completed: number;
  segments_generated: number;
  rewards_generated: number;
  promotions_generated: number;
  actions_generated: number;
  commercial_opportunities_generated: number;
  segment_job_id?: number;
  ds_segments_job_id?: number;
  next_purchase_job_id?: number;
  rewards_job_id?: number;
  promotions_job_id?: number;
  error_message?: string;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  updated_at: string;
  active: boolean;
}

export interface RecommendationJobSelection {
  job_type: string;
  job_id: number;
  job_name: string;
  organization_id: number;
  is_active: boolean;
}

export interface RecommendationFlowStep {
  step: string;
  label: string;
  label_key?: string | null;
  enabled: boolean;
  toggleable: boolean;
  job_type?: string;
  job_configured?: boolean;
}

export interface RecommendationFlowConfig {
  organization_id: number;
  segment_engine_enabled: boolean;
  ds_segments_enabled: boolean;
  commercial_opportunities_enabled: boolean;
  next_purchase_enabled: boolean;
  rewards_enabled: boolean;
  promotions_enabled: boolean;
  activation_actions_enabled: boolean;
  steps: RecommendationFlowStep[];
}

export interface UpdateRecommendationFlowConfigRequest {
  segment_engine_enabled?: boolean;
  ds_segments_enabled?: boolean;
  commercial_opportunities_enabled?: boolean;
  next_purchase_enabled?: boolean;
  rewards_enabled?: boolean;
  promotions_enabled?: boolean;
  activation_actions_enabled?: boolean;
  organization_id?: number;
}

export interface RecommendationRunHistoryPage {
  recommendationRuns: RecommendationRun[];
  totalElements: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

export class RecommendationRunApi {
  static async startRun(): Promise<RecommendationRun> {
    const response = await client.post(
      "/api/recommendations/run",
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      },
    );
    return response.data;
  }

  static async getActiveOrLatest(): Promise<RecommendationRun | null> {
    try {
      const response = await client.get("/api/recommendations/run/active", {
        headers: {
          Authorization: getToken(),
        },
        validateStatus: (status) => status === 200 || status === 204,
      });
      return response.status === 204 ? null : response.data;
    } catch (error) {
      console.error("Error fetching recommendation run:", error);
      throw error;
    }
  }

  static async getRun(runId: number): Promise<RecommendationRun> {
    const response = await client.get(`/api/recommendations/run/${runId}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async getHistory(
    page = 1,
    size = 10,
  ): Promise<RecommendationRunHistoryPage> {
    const response = await client.get("/api/recommendations/run/history", {
      headers: {
        Authorization: getToken(),
      },
      params: {
        page,
        size,
      },
    });
    return response.data;
  }

  static async cancelRun(runId: number): Promise<RecommendationRun> {
    const response = await client.post(
      `/api/recommendations/run/${runId}/cancel`,
      {},
      {
        headers: {
          Authorization: getToken(),
        },
      },
    );
    return response.data;
  }

  static async getJobSelections(
    organizationId?: number,
  ): Promise<RecommendationJobSelection[]> {
    const params = organizationId ? `?organization_id=${organizationId}` : "";
    const response = await client.get(`/api/recommendations/run/job-selections${params}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async setJobSelection(
    jobType: string,
    jobId: number,
    organizationId?: number,
  ): Promise<RecommendationJobSelection> {
    const response = await client.put(
      `/api/recommendations/run/job-selections/${jobType}`,
      { job_id: jobId, organization_id: organizationId },
      {
        headers: {
          Authorization: getToken(),
        },
      },
    );
    return response.data;
  }

  static async clearJobSelection(
    jobType: string,
    organizationId?: number,
  ): Promise<void> {
    const params = organizationId ? `?organization_id=${organizationId}` : "";
    await client.delete(`/api/recommendations/run/job-selections/${jobType}${params}`, {
      headers: {
        Authorization: getToken(),
      },
    });
  }

  static async getFlowConfig(
    organizationId?: number,
  ): Promise<RecommendationFlowConfig> {
    const params = organizationId ? `?organization_id=${organizationId}` : "";
    const response = await client.get(`/api/recommendations/run/flow-config${params}`, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }

  static async updateFlowConfig(
    config: UpdateRecommendationFlowConfigRequest,
  ): Promise<RecommendationFlowConfig> {
    const response = await client.put("/api/recommendations/run/flow-config", config, {
      headers: {
        Authorization: getToken(),
      },
    });
    return response.data;
  }
}
